"use client";
import React, { useEffect, useState } from "react";
import ArrowLinkButtonDesk from "../ui/ArrowLinkButtonDesk";
import ArrowLinkButtonMob from "../ui/ArrowLinkButtonMob";
import MarketCap from "./MarketCap";
import { apiPost } from "@/utils/apiClient";
import SectionHeading from "./SectionHeading";
import { formatDate } from "@/utils/commonFunctions";
import InformationButton from "@/components/ui/InformationButton";

type SectionPortfolio = {
  fundSlug: string;
  msoCode: string;
  schemeCode: string;
  amcCode: string;
};

type PortfolioItem = {
  instrument: string;
  holdings: number;
};

type PortfolioResponse = {
  portfolio_date: string;

  result?: {
    sector: PortfolioItem[];
    company: PortfolioItem[];
    market_cap?: {
      holdings: number;
      instrument: string;
    }[];
  }[];
  tabs?: {
    name: string;
    allocation: number;
  }[];
};

export default function PortfolioSectionOverview({ fundSlug, msoCode, schemeCode, amcCode }: SectionPortfolio) {
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [portfolioData, setPortfolioData] = useState<PortfolioResponse["result"]>([]);
  const [tabList, setTabList] = useState<{ tabName: string; allocation: number }[]>([]);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const payload = {
          scheme_code: schemeCode,
          amc_code: amcCode,
        };

        const data = await apiPost<PortfolioResponse>("fundPortfolioBasic", payload);

        setData(data);
        setPortfolioData(data.result || []);

        if (data.tabs?.length) {
          const mappedTabs = data.tabs.map((tab) => ({
            tabName: tab.name,
            allocation: tab.allocation,
          }));
          setTabList(mappedTabs);
        }
      } catch (err) {
        console.error("Failed to fetch portfolio data:", err);
      }
    };

    fetchPortfolio();
  }, [schemeCode, amcCode]);

  if (!data || !data?.result || data.result?.length <= 0) return;

  return (
    <section className="mb-8 xs:mb-12">
      <div className="flex items-center gap-3 mb-2 sm:mb-4">
        <SectionHeading heading="Portfolio Analysis" />
        <InformationButton keywords="portfolio-analysis" />

        <ArrowLinkButtonDesk href={`/mutual-fund/${fundSlug}/portfolio-details/${msoCode}`} text="View Detailed Portfolio" />
      </div>

      <MarketCap PortfolioData={portfolioData || []} tabList={tabList} fundSlug={fundSlug} msoCode={msoCode} />

      <p className="hidden sm:block text-xs text-[#6E6E73] font-medium mt-2.5">As of {formatDate(data.portfolio_date)}</p>

      <ArrowLinkButtonMob href={`/mutual-fund/${fundSlug}/portfolio-details/${msoCode}`} text="View Detailed Portfolio" className="mt-5" />
    </section>
  );
}
